<template>
  <div class="flex flex-col min-h-screen font-Roboto bg-genesys-primary">
    <Navigation />

    <Login v-if="!isAuthenticated" />
    <OnboardingView v-else-if="!isOnboardingComplete" />
    <template v-else>
      <RouterView />
      <FeedbackModal
          v-if="feedbackVisible"
          @submitted="onFeedbackSubmitted"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { RouterView } from "vue-router";
import Navigation from "@/components/Navigation.vue";
import Login from "@/components/Login.vue";
import OnboardingView from "@/components/OnboardingView.vue";
import FeedbackModal from "@/components/FeedbackModal.vue";
import { useAuthStore } from "@/stores/authStore.ts";
import { useUserSettingsStore } from "@/stores/userSettingsStore";
import { computed, onMounted, ref, watch } from "vue";

const authStore = useAuthStore();
const settingsStore = useUserSettingsStore();

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isOnboardingComplete = computed(() => settingsStore.isOnboardingComplete);

// ── Feedback modal ────────────────────────────────────────────────────────────
const FEEDBACK_DONE_KEY = 'feedback_done';

const feedbackVisible = ref(false);

watch(isOnboardingComplete, (complete) => {
  if (complete && localStorage.getItem(FEEDBACK_DONE_KEY) !== 'true') {
    feedbackVisible.value = true;
  }
}, { immediate: true });

const onFeedbackSubmitted = () => {
  feedbackVisible.value = false;
  localStorage.setItem(FEEDBACK_DONE_KEY, 'true');
};

const initializeGenesys = (): void => {
  // Only initialize if not already loaded
  if (window.Genesys) return;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.innerHTML = `
    (function (g, e, n, es, ys) {
      g['_genesysJs'] = e;
      g[e] = g[e] || function () {
        (g[e].q = g[e].q || []).push(arguments)
      };
      g[e].t = 1 * new Date();
      g[e].c = es;
      ys = document.createElement('script'); ys.async = 1; ys.src = n; ys.charset = 'utf-8'; document.head.appendChild(ys);
    })(window, 'Genesys', 'https://apps.mypurecloud.com/genesys-bootstrap/genesys.min.js', {
      environment: 'use1',
      deploymentId: '8b8e5786-b836-4f7e-9412-2c67f255bacc'
    })
  `;

  // Add onload handler
  script.onload = () => {
    if (window.Genesys) {
      // Configure plugins path
      window.Genesys("configure", {
        pluginsPath: "https://apps.mypurecloud.com/genesys-web-messaging/plugins/",
        supportedPlugins: ["Knowledge"]
      });
    }
  };

  document.head.appendChild(script);
};

onMounted(() => {
  initializeGenesys();
});
</script>